import type { DropShadowEffect, LayerBlurEffect } from "@/document/model/styles";
import { applyAffinePoint, type Affine2D } from "@/lib/math/affine";
import { fitClipPolygonToUniformLimit, maxShaderClipPointCount } from "@/lib/math/clipPolygon";
import type { DisplayClip, DisplayItem } from "./displayList";
import type { RenderPreview } from "../overlayTypes";
import { formatCacheNumber } from "../renderHelpers";

export { maxShaderClipPointCount } from "@/lib/math/clipPolygon";
export { fitClipPolygonToUniformLimit } from "@/lib/math/clipPolygon";

export function clipUniforms(clip: DisplayClip | undefined): Record<string, unknown> {
  const maxClipPointCount = maxShaderClipPointCount;
  const points = new Array<number>(maxClipPointCount * 2).fill(0);
  if (!clip || clip.polygon.length < 3) {
    return {
      u_clipPointCount: 0,
      u_clipPoints: points,
    };
  }

  const polygon = fitClipPolygonToUniformLimit(clip.polygon, maxClipPointCount);
  polygon.forEach((point, index) => {
    points[index * 2] = point.x;
    points[index * 2 + 1] = point.y;
  });
  return {
    u_clipPointCount: polygon.length,
    u_clipPoints: points,
  };
}

export function shouldRasterizeClip(clip: DisplayClip | undefined): clip is DisplayClip {
  return Boolean(clip && clip.polygon.length > maxShaderClipPointCount);
}

export function applyCanvasClip(context: CanvasRenderingContext2D, clip: DisplayClip): void {
  const first = clip.polygon[0];
  if (!first) {
    return;
  }

  context.beginPath();
  context.moveTo(first.x, first.y);
  for (let index = 1; index < clip.polygon.length; index += 1) {
    const point = clip.polygon[index]!;
    context.lineTo(point.x, point.y);
  }
  context.closePath();
  context.clip("evenodd");
}

export function displayItemIntersectsViewport(item: DisplayItem, viewportBounds: RenderPreview["bounds"]): boolean {
  const visualBounds = displayItemVisualBounds(item);
  const bounds = item.clip ? intersectBounds(visualBounds, item.clip.bounds) : visualBounds;
  return rectsIntersect(bounds, viewportBounds);
}

export function intersectBounds(left: RenderPreview["bounds"], right: RenderPreview["bounds"]): RenderPreview["bounds"] {
  const x1 = Math.max(left.x, right.x);
  const y1 = Math.max(left.y, right.y);
  const x2 = Math.min(left.x + left.width, right.x + right.width);
  const y2 = Math.min(left.y + left.height, right.y + right.height);
  return {
    x: x1,
    y: y1,
    width: Math.max(0, x2 - x1),
    height: Math.max(0, y2 - y1),
  };
}

export function rectsIntersect(left: RenderPreview["bounds"], right: RenderPreview["bounds"]): boolean {
  return left.x < right.x + right.width &&
    left.x + left.width > right.x &&
    left.y < right.y + right.height &&
    left.y + left.height > right.y;
}

export function displayItemVisualBounds(item: DisplayItem): RenderPreview["bounds"] {
  const baseBounds = item.visualBounds ?? item.bounds;
  return (item.effects ?? []).reduce(
    (bounds, effect) => {
      if (!effect.visible) {
        return bounds;
      }
      if (effect.type === "dropShadow") {
        return unionBounds(bounds, dropShadowBounds(baseBounds, effect));
      }
      if (effect.type === "layerBlur") {
        return unionBounds(bounds, expandedBounds(baseBounds, layerBlurRadius(effect) * 2));
      }
      return bounds;
    },
    baseBounds,
  );
}

export function dropShadowBounds(bounds: RenderPreview["bounds"], effect: DropShadowEffect): RenderPreview["bounds"] {
  // Pad by ~3σ; document blur maps to σ via DOCUMENT_BLUR_TO_GAUSSIAN_SIGMA (=2).
  const radius = Math.max(0, effect.blur) * 6 + Math.max(0, effect.spread);
  const left = bounds.x + effect.offsetX - radius;
  const top = bounds.y + effect.offsetY - radius;
  return {
    x: left,
    y: top,
    width: bounds.width + radius * 2,
    height: bounds.height + radius * 2,
  };
}

export function dropShadowCacheKey(item: DisplayItem, effect: DropShadowEffect, scale: number, generation: number): string {
  const color = effect.color;
  const colorKey = typeof color === "string"
    ? color
    : `${color.l}/${color.c}/${color.h}/${color.alpha ?? 1}`;
  return [
    generation,
    item.geometryKey ?? dropShadowGeometryKey(item),
    effect.blur,
    effect.spread,
    effect.offsetX,
    effect.offsetY,
    colorKey,
    formatCacheNumber(scale),
  ].join("|");
}

export function dropShadowGeometryKey(item: DisplayItem): string {
  const anchor = item.visualBounds ?? item.bounds;
  const originX = anchor.x;
  const originY = anchor.y;
  const contourKey = item.shadowContours
    ? item.shadowContours.map(contour => contour.map(point => `${formatCacheNumber(point.x - originX)},${formatCacheNumber(point.y - originY)}`).join(";")).join("|")
    : "none";
  const vertexSamples: string[] = [];
  for (let index = 0; index < item.vertices.length && vertexSamples.length < 8; index += Math.max(6, Math.floor(item.vertices.length / 8) || 6)) {
    vertexSamples.push(`${formatCacheNumber(item.vertices[index]! - originX)},${formatCacheNumber(item.vertices[index + 1]! - originY)}`);
  }
  const radiiKey = item.cornerRadii?.join(",") ?? "none";
  return `${item.kind}|${item.vertices.length}|${contourKey}|${vertexSamples.join(";")}|${radiiKey}|${formatCacheNumber(anchor.width)}x${formatCacheNumber(anchor.height)}`;
}

export function unionBounds(left: RenderPreview["bounds"], right: RenderPreview["bounds"]): RenderPreview["bounds"] {
  const x1 = Math.min(left.x, right.x);
  const y1 = Math.min(left.y, right.y);
  const x2 = Math.max(left.x + left.width, right.x + right.width);
  const y2 = Math.max(left.y + left.height, right.y + right.height);
  return { x: x1, y: y1, width: x2 - x1, height: y2 - y1 };
}

export function expandedBounds(bounds: RenderPreview["bounds"], amount: number): RenderPreview["bounds"] {
  const padding = Math.max(0, amount);
  return {
    x: bounds.x - padding,
    y: bounds.y - padding,
    width: bounds.width + padding * 2,
    height: bounds.height + padding * 2,
  };
}

export function layerBlurRadius(effect: LayerBlurEffect): number {
  return Math.max(0, effect.mode === "progressive" ? Math.max(effect.start, effect.end) : effect.blur);
}

/**
 * Reduce a layer-blur rasterization scale so the resulting device-space blur
 * radius (`blur * scale`) never exceeds `maxBlurDevicePx`.
 *
 * Prefer {@link effectRasterScale} for shadows/blurs — it picks a zoom-independent
 * scale so the same document blur looks identical at 100% and 200%.
 */
export function boundedLayerBlurRasterScale(scale: number, blur: number, maxBlurDevicePx: number): number {
  if (blur <= 0 || blur * scale <= maxBlurDevicePx) {
    return scale;
  }
  return Math.max(0.05, maxBlurDevicePx / blur);
}

/**
 * Raster scale for soft effects so document-space blur is zoom-invariant.
 *
 * Soft shadows/blurs are low-frequency: once device σ ≈ `targetBlurSigma`, extra
 * zoom only wastes memory and previously made high zoom look sharper (kernel cap)
 * or low zoom look mushier (different texel σ). Scale is derived from the blur
 * radius alone (clamped by texture limits), not from camera zoom.
 */
export function effectRasterScale(
  blurDoc: number,
  bounds: { width: number; height: number },
  maxTextureSize: number,
  targetBlurSigma: number,
): number {
  const byTexture = Math.min(
    maxTextureSize / Math.max(1, bounds.width),
    maxTextureSize / Math.max(1, bounds.height),
  );
  if (blurDoc <= 1e-6) {
    return Math.max(0.25, Math.min(1, byTexture));
  }
  const byBlur = targetBlurSigma / blurDoc;
  return Math.max(0.25, Math.min(byBlur, byTexture));
}

export function transformRectVertices(bounds: RenderPreview["bounds"], transform: Affine2D): number[] {
  return [
    { x: bounds.x, y: bounds.y },
    { x: bounds.x + bounds.width, y: bounds.y },
    { x: bounds.x, y: bounds.y + bounds.height },
    { x: bounds.x, y: bounds.y + bounds.height },
    { x: bounds.x + bounds.width, y: bounds.y },
    { x: bounds.x + bounds.width, y: bounds.y + bounds.height },
  ].flatMap(point => {
    const transformed = applyAffinePoint(transform, point);
    return [transformed.x, transformed.y];
  });
}
